import i18next from 'i18next';
import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { DISPLAY_MODE } from '@/main/app.constants';
import { INVESTIGATE_TOOLS, TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import type { InvestigateToolType } from '@/toolSelection/investigate.constants';
import type { TItemProperties } from '@/tools/itemProperties/hooks/useProperties';

interface DerivedDataTreeEntry {
  id: string;
  name?: string;
  color?: string | null;
  children?: DerivedDataTreeEntry[];
}

type InvestigateItem = Partial<TItemProperties> & {
  color?: string | null;
  iconClass?: string;
  calculationType?: string;
  isArchived?: boolean;
  errorType?: string;
};
type DerivedDataTree = DerivedDataTreeEntry[] | null;
type TrendToolId = string;
type DisplayMode = string;

interface InvestigateSetItemPayload {
  item: Partial<TItemProperties>;
  props?: Partial<InvestigateItem>;
}

interface InvestigateSetActiveToolPayload {
  tool: TrendToolId;
}

interface InvestigateSetToolFilterPayload {
  filter: string;
}

interface InvestigateSetDisplayModePayload {
  mode: DisplayMode;
}

interface InvestigateSetDerivedDataTreePayload {
  derivedDataTree: DerivedDataTree;
}

interface TrendSetColorPayload {
  id: string;
  color: string;
}

interface DerivedDataPanelOpenPayload {
  derivedDataPanelOpen: boolean;
}

interface InvestigateState {
  item: InvestigateItem;
  activeTool: TrendToolId;
  toolFilter: string;
  displayMode: DisplayMode;
  derivedDataPanelOpen: boolean;
  toolErrorMessage: string;
  addOnTools: InvestigateToolType[];
  pluginTools: InvestigateToolType[];
  derivedDataTree: DerivedDataTree;
  investigateTools: InvestigateToolType[];
  modelsEnabledForUser: boolean;
  allTools: any; // monkey() computed
  filteredTools: any; // monkey() computed
  breadcrumbs: any; // monkey() computed
  isCapsulePickingMode: any; // monkey() computed
  activeToolName: any; // monkey() computed
}

export type InvestigateDehydratedState = Pick<
  InvestigateState,
  'item' | 'activeTool' | 'displayMode' | 'derivedDataPanelOpen'
>;

export class InvestigateStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqInvestigateStore';

  initialize(initializeMode: InitializeMode): void {
    const saveState = this.state && initializeMode !== 'FORCE';
    const initialState: InvestigateState = {
      item: saveState ? (this.state.get('item') as InvestigateItem) : ({} as InvestigateItem),
      activeTool: TREND_TOOLS.OVERVIEW,
      toolFilter: '',
      displayMode: DISPLAY_MODE.NEW,
      derivedDataPanelOpen: false,
      toolErrorMessage: '',
      // Add-on tools need to maintain their state across soft and hard initialization so reloading previous
      // worksteps does not clear add-on tools. Add-on tools are set whenever a new Analysis worksheet is
      // loaded so they're guaranteed to be updated for the current user whenever the user displays a worksheet.
      addOnTools: (this.state && (this.state.get('addOnTools') as InvestigateToolType[])) ?? [],
      pluginTools: (this.state && (this.state.get('pluginTools') as InvestigateToolType[])) ?? [],
      // Save asynchronously loaded data to prevent fast follow flicker
      derivedDataTree: saveState ? (this.state.get('derivedDataTree') as DerivedDataTree) : null,
      investigateTools: this.translateTools(),
      // Models enabled flag is set when add-on tools are loaded
      modelsEnabledForUser: (this.state && (this.state.get('modelsEnabledForUser') as boolean)) ?? false,
      allTools: this.monkey(['addOnTools'], ['pluginTools'], ['investigateTools'], (addOnTools, pluginTools) =>
        ((this.state?.get('investigateTools') as InvestigateToolType[]) ?? [])
          .concat(addOnTools as InvestigateToolType[])
          .concat(pluginTools as InvestigateToolType[]),
      ),
      filteredTools: this.monkey(
        ['item'],
        ['toolFilter'],
        ['activeTool'],
        ['addOnTools'],
        ['pluginTools'],
        ['allTools'],
        ['investigateTools'],
        ['modelsEnabledForUser'],
        (
          _item: InvestigateItem,
          toolFilter: string,
          activeTool: TrendToolId,
          addOnTools: InvestigateToolType[],
          pluginTools: InvestigateToolType[],
          allTools: InvestigateToolType[],
          _investigateTools: InvestigateToolType[],
          modelsEnabledForUser: boolean,
        ) => {
          const filterByParent = (tool: InvestigateToolType) => tool.parentId === activeTool;
          const filterByText = (tool: InvestigateToolType) =>
            _.includes(_.toLower(tool.name ?? ''), _.toLower(toolFilter)) ||
            _.includes(_.toLower(tool.description ?? ''), _.toLower(toolFilter));
          const toolFilterFunc: (tool: InvestigateToolType) => boolean = _.isEmpty(toolFilter)
            ? filterByParent
            : filterByText;
          const isAddOnToolsGroupWithNoAddOnTools = (tool: InvestigateToolType) =>
            tool.id === TREND_TOOLS.ADDON_TOOLS_GROUP && addOnTools.length === 0 && pluginTools.length === 0;
          const isModelsToolWithModelsDisabled = (tool: InvestigateToolType) =>
            (tool.id === 'models' || tool.id === 'model-create' || tool.id === 'model-apply') && !modelsEnabledForUser;

          const filteredTools = _.chain(allTools ?? [])
            .reject('hideFromSearch')
            .filter(toolFilterFunc)
            // used for search into a trend tool
            .filter((tool) =>
              activeTool !== TREND_TOOLS.OVERVIEW && !_.isEmpty(toolFilter) ? filterByParent(tool) : true,
            )
            .reject(isAddOnToolsGroupWithNoAddOnTools)
            .reject(isModelsToolWithModelsDisabled)
            .value();

          // show scorecard tool when boundaries is searched for
          if (
            toolFilter &&
            (activeTool === TREND_TOOLS.OVERVIEW || activeTool === TREND_TOOLS.QUANTIFY) &&
            'boundaries'.indexOf(_.toLower(toolFilter)) !== -1 &&
            _.findIndex(filteredTools, { id: 'threshold-metric' }) === -1
          ) {
            const thresholdTool = _.find(allTools, { id: 'threshold-metric' });
            if (thresholdTool) {
              filteredTools.push(thresholdTool);
            }
          }

          return filteredTools;
        },
        { immutable: false },
      ),

      breadcrumbs: this.monkey(['activeTool'], ['toolFilter'], ['allTools'], (activeTool, _toolFilter, allTools) => {
        const resolvedTools = (allTools as InvestigateToolType[]) ?? [];
        const breadcrumbs: InvestigateToolType[] = [];
        let crumb = _.find(resolvedTools, ['id', activeTool]) as InvestigateToolType | undefined;

        while (crumb) {
          breadcrumbs.push(crumb);
          crumb = _.find(resolvedTools, ['id', crumb.parentId]) as InvestigateToolType | undefined;
        }

        return _.reverse(breadcrumbs);
      }),
      isCapsulePickingMode: this.monkey(['activeTool'], (activeTool) =>
        _.includes([TREND_TOOLS.MANUAL_CONDITION], activeTool),
      ),
      activeToolName: this.monkey(['activeTool'], ['allTools'], (activeTool, allTools) => {
        return _.get(_.find(allTools, ['id', activeTool]), 'name');
      }),
    };
    this.state = this.immutable(initialState, { immutable: false });
  }

  get item(): InvestigateItem {
    return this.state.get('item') as InvestigateItem;
  }

  get activeTool(): TrendToolId {
    // ensure the active tool is a valid tool. in case a tool gets renamed it will typically be caught in the
    // config upgrader, however, if we missed adding an upgrade or if the affected tool was the active tool
    // the config upgrade will not be applied and we can end up with a blank tool pane. To avoid this we check
    // the active tool and revert to the overview if necessary
    const activeTool = this.state.get('activeTool');
    if (activeTool !== TREND_TOOLS.OVERVIEW) {
      const allTools = (this.state.get('allTools') as InvestigateToolType[]) ?? [];
      const isActiveToolInvalid = !_.chain(allTools).map('id').includes(this.state.get('activeTool')).value();
      if (isActiveToolInvalid) {
        this.state.set('activeTool', TREND_TOOLS.OVERVIEW);
      }
    }
    return this.state.get('activeTool') as TrendToolId;
  }

  get activeToolName(): string | undefined {
    return this.state.get('activeToolName') as string | undefined;
  }

  get filteredTools(): InvestigateToolType[] {
    return (this.state.get('filteredTools') as InvestigateToolType[]) ?? [];
  }

  get breadcrumbs(): InvestigateToolType[] {
    return (this.state.get('breadcrumbs') as InvestigateToolType[]) ?? [];
  }

  get toolFilter(): string {
    return this.state.get('toolFilter') as string;
  }

  get displayMode(): DisplayMode {
    return this.state.get('displayMode') as DisplayMode;
  }

  get derivedDataPanelOpen(): boolean {
    return this.state.get('derivedDataPanelOpen') as boolean;
  }

  get derivedDataTree(): DerivedDataTree {
    return this.state.get('derivedDataTree') as DerivedDataTree;
  }

  get isCapsulePickingMode(): boolean {
    return this.state.get('isCapsulePickingMode') as boolean;
  }

  get allTools(): InvestigateToolType[] {
    return (this.state.get('allTools') as InvestigateToolType[]) ?? [];
  }

  get addOnTools(): InvestigateToolType[] {
    return (this.state.get('addOnTools') as InvestigateToolType[]) ?? [];
  }

  get pluginTools(): InvestigateToolType[] {
    return (this.state.get('pluginTools') as InvestigateToolType[]) ?? [];
  }

  get toolErrorMessage(): string {
    return this.state.get('toolErrorMessage') as string;
  }

  dehydrate(): InvestigateDehydratedState {
    return {
      item: (this.state.get('item') as InvestigateItem) ?? ({} as InvestigateItem),
      activeTool: this.state.get('activeTool') as TrendToolId,
      displayMode: this.state.get('displayMode') as DisplayMode,
      derivedDataPanelOpen: this.state.get('derivedDataPanelOpen') as boolean,
    };
  }

  rehydrate(dehydratedState: Partial<InvestigateDehydratedState>): void {
    if ('item' in dehydratedState && dehydratedState.item !== undefined) {
      this.state.set('item', dehydratedState.item);
    }
    if ('activeTool' in dehydratedState && dehydratedState.activeTool !== undefined) {
      this.state.set('activeTool', dehydratedState.activeTool);
    }
    if ('displayMode' in dehydratedState && dehydratedState.displayMode !== undefined) {
      this.state.set('displayMode', dehydratedState.displayMode);
    }
    if ('derivedDataPanelOpen' in dehydratedState && dehydratedState.derivedDataPanelOpen !== undefined) {
      this.state.set('derivedDataPanelOpen', dehydratedState.derivedDataPanelOpen);
    }
  }

  /**
   * Utility function that translates the investigate tools headline and description.
   */
  translateTools(): InvestigateToolType[] {
    return _.map(INVESTIGATE_TOOLS, (tool) => {
      const resolvedName = tool.nameTranslationKey ? i18next.t(tool.nameTranslationKey) : (tool.name ?? '');
      const resolvedDescription = tool.descriptionTranslationKey
        ? i18next.t(tool.descriptionTranslationKey)
        : (tool.description ?? '');

      return _.assign({}, tool, {
        name: resolvedName,
        description: resolvedDescription,
      });
    });
  }

  /**
   * Since tools can change a lot, only keep the persisted active tool if it is still valid
   */
  validateActiveTool(): void {
    const isActiveToolInvalid = !_.chain((this.state.get('allTools') as InvestigateToolType[]) ?? [])
      .map('id')
      .includes(this.state.get('activeTool'))
      .value();

    if (isActiveToolInvalid) {
      this.state.set('activeTool', TREND_TOOLS.OVERVIEW);
    }
  }

  protected readonly handlers = {
    INVESTIGATE_SET_ITEM: (payload: InvestigateSetItemPayload) => {
      this.state.set(
        'item',
        _.assign(
          {},
          payload.props,
          _.pick(payload.item, ['id', 'name', 'itemType', 'iconClass', 'calculationType', 'isArchived']),
        ),
      );
    },
    INVESTIGATE_CLEAR_ITEM: () => {
      this.state.set('item', {} as InvestigateItem);
    },
    INVESTIGATE_SET_ACTIVE_TOOL: (payload: InvestigateSetActiveToolPayload) => {
      this.state.set('activeTool', payload.tool);
      if (payload.tool !== TREND_TOOLS.OVERVIEW) {
        this.state.set('toolFilter', '');
      }
    },
    INVESTIGATE_SET_TOOL_FILTER: (payload: InvestigateSetToolFilterPayload) => {
      this.state.set('toolFilter', payload.filter);
      this.state.set('item', {} as InvestigateItem);
      this.state.commit();
    },
    INVESTIGATE_SET_DISPLAY_MODE: (payload: InvestigateSetDisplayModePayload) => {
      this.state.set('displayMode', payload.mode);
    },
    INVESTIGATE_SET_DERIVED_DATA_PANEL_OPEN: (payload: DerivedDataPanelOpenPayload) => {
      this.state.set('derivedDataPanelOpen', payload.derivedDataPanelOpen);
    },
    /**
     * Sets the derived data tree. The tree is not persisted as part of the workstep.
     *
     * @param {Object} payload - Object container for arguments
     * @param {Object[]} payload.derivedDataTree - Array of items that form the derived data tree
     */
    INVESTIGATE_SET_DERIVED_DATA_TREE: (payload: InvestigateSetDerivedDataTreePayload) => {
      this.state.set('derivedDataTree', payload.derivedDataTree);
    },
    /**
     * Sets the array of add-on tools and validates the active tool. Must be called after rehydrate.
     */
    INVESTIGATE_SET_ADDON_TOOLS: (payload: { addOnTools: InvestigateToolType[] }) => {
      this.state.set('addOnTools', payload.addOnTools);
      this.validateActiveTool();
    },
    /**
     * Sets the array of plugin tools and validates the active tool. Must be called after rehydrate.
     */
    INVESTIGATE_SET_PLUGIN_TOOLS: (payload: { pluginTools: InvestigateToolType[] }) => {
      this.state.set('pluginTools', payload.pluginTools);
      this.validateActiveTool();
    },
    /**
     * Sets whether the Models feature is enabled for the current user.
     * This flag is used to filter out model-related tools when models are not available.
     */
    INVESTIGATE_SET_MODELS_ENABLED_FOR_USER: (payload: { modelsEnabledForUser: boolean }) => {
      this.state.set('modelsEnabledForUser', payload.modelsEnabledForUser);
    },
    TREND_SET_COLOR: ({ id, color }: TrendSetColorPayload) => {
      const derivedDataTree = this.state.get('derivedDataTree') as DerivedDataTree;
      if (!Array.isArray(derivedDataTree)) {
        return;
      }

      const dependencyTree: DerivedDataTreeEntry[] = derivedDataTree.map((item) => ({ ...item }));
      const entry = _.find(dependencyTree, { id }) as DerivedDataTreeEntry | undefined;
      if (entry) {
        entry.color = color;
        this.state.set('derivedDataTree', dependencyTree);
      }
    },
    INVESTIGATE_TRIGGER_INVESTIGATE_TOOL_TRANSLATION: () => {
      const translatedTools = this.translateTools();
      this.state.set('investigateTools', translatedTools);
    },
    INVESTIGATE_SET_TOOL_ERROR_MESSAGE: (payload: { message: string }) => {
      this.state.set('toolErrorMessage', payload.message);
    },
  };
}
